import * as React from 'react';
import { Navigate } from 'react-router-dom';
// Subviews
import { HomeView } from '/@/app/views/HomeView';
import { LabsView } from '/@/app/views/LabsView';
import { LabTestsView } from '/@/app/views/LabTestsView';
import { PlanView } from '/@/app/views/PlanView';
import { QuestionsView } from '/@/app/views/QuestionsView';
import { RisksView } from '/@/app/views/RisksView';
import { AgeAssessmentView } from '/@/app/views/AgeAssessmentView';
import { NextView } from '/@/app/views/NextView';
import { PanelsView } from '/@/app/views/PanelsView';
import { DynamicsView } from '/@/app/views/DynamicsView';
import { FreeMeasurementsView } from '/@/app/views/FreeMeasurementsView';
import { AgeCalculatorView } from '/@/app/views/AgeCalculatorView';
// Records
import { RecordsView } from '/@/app/views/RecordsView';
import { RecordsInputView } from '/@/app/views/RecordsInputView';
// Chatbot
import { ChatbotView } from '/@/app/views/ChatbotView';
// Secondary views
import { ProfileView } from '/@/app/views/ProfileView';
import { FinishRegistrationView } from '/@/app/views/FinishRegistrationView';
import { AcceptTermsView } from '/@/app/views/AcceptTermsView';
import { IntroView } from '/@/app/views/IntroView';
import { CompanyDetailsView } from '/@/app/views/CompanyDetailsView'
// Subscriptions-related
import { SubscriptionsView } from '/@/app/views/Subscriptions/SubscriptionsView';
import { SubscriptionsHistoryView } from '/@/app/views/Subscriptions/SubscriptionsHistoryView';
import { PaymentCancelledView } from '/@/app/views/Payment/PaymentCancelledView';
import { PaymentFailureView } from '/@/app/views/Payment/PaymentFailureView';
import { PaymentInprogressView } from '/@/app/views/Payment/PaymentInprogressView';
import { PaymentSuccessView } from '/@/app/views/Payment/PaymentSuccessView';
// debug/admin subviews
import { TesterView } from '/@/app/views/TesterView';
import { Actions as TestActions } from '/@/app/views/TesterView/Actions';
import { Style as TestStyle } from '/@/app/views/TesterView/Style';
import { DynamicsCasesMain } from '/@/app/views/TesterView/DynamicsCasesMain';
import { DynamicsCasesTypical } from '/@/app/views/TesterView/DynamicsCasesTypical';
import { DynamicsCasesSelect } from '/@/app/views/TesterView/DynamicsCasesBiohacker';
import { userFlowState } from '/@/app/states/userFlowState';

import { freeMeasurementsRoutes } from '/@/app/views/FreeMeasurementsView/routes';

// Base Path for everything
const appPath = "/cabinet";

//
// Ensures that the path exists OR routes to default
//
export const route = (path) => {
    let fullPath = appPath + path;
    if (!fullPath.endsWith("/")) {
        fullPath += "/";
    }

    const itemsFound = contentPaneRoutes.filter((route) =>
        // route.path === fullPath
        fullPath.startsWith(route.path)
    );
    if (itemsFound.length === 0) {
        const error = new Error();
        console.log("Route not found: "+fullPath, "\n", error.stack);
        return appPath+"/";
    }

    if (itemsFound[0].length < fullPath) {
        // FIXME check children. They can be dynamic though.
    }

    return fullPath;
}


export const routeAttrs = (location) => {
    let path = location.pathname;
    if (!path.endsWith('/')) {
        path += '/';
        // XXX missing slash caused error on Stage
    }

    // full path search
    for (let route of contentPaneRoutes) {
        if (route.path === path) {
            return route;
        }
    }
    // dynamic segments, e.g. '/cabinet/plan/:program/'
    for (let route of contentPaneRoutes) {
        if (route.path.includes(':') && new RegExp('^' + route.path.replace(/:[^/]+/g, '[^/]+') + '$').test(path)) {
            return route;
        }
    }
    // draft partial path search
    for (let route of contentPaneRoutes) {
        if (route.children && path.startsWith(route.path)) {
            return route;
        }
    }
}

//
// Main routes
//
export const contentPaneRoutes = [
    {
        path: appPath+"/",
        element: <HomeView userFlowState={userFlowState} />,
    },

    //
    // Main Steps
    //
    {
        path: appPath+"/labs/",
        element: <LabsView />,
    },
    {
        path: appPath+"/questions/",
        element: <QuestionsView />,
    },
    {
        path: appPath+"/questions/unanswered/",
        element: <QuestionsView answeringMode="unanswered" />,
    },
    {
        path: appPath+"/lab-tests/",
        element: <LabTestsView />,
        _saveScroll: true,
    },
    {
        path: appPath+"/risks/",
        element: <RisksView />,
        _saveScroll: true,
        _resetScrollOnCalculate: true,
    },
    {
        path: appPath+"/age-assessments/",
        element: <AgeAssessmentView />,
        _saveScroll: true,
        _resetScrollOnCalculate: true,
    },
    {
        path: appPath+"/plan/",
        element: <PlanView />,
        _saveScroll: true,
        _resetScrollOnCalculate: true,
    },
    {
        path: appPath+"/plan/:program/",
        element: <PlanView />,  // program filter comes later; for now shows the whole plan
        _saveScroll: true,
        _resetScrollOnCalculate: true,
    },
    {
        path: appPath+"/next/",
        element: <NextView />,
    },

    //
    // Other main functional views
    //
    {
        path: appPath+"/dynamics/",
        element: <DynamicsView />,
    },
    {
        path: appPath+"/panels/",
        element: <PanelsView />,
    },
    {
        path: appPath+"/free/",
        element: <FreeMeasurementsView />,
        children: freeMeasurementsRoutes,  // subviews controlled by state
        _hideCurrentPanelBar: true,
    },

    //
    // Records views
    //
    {
        path: appPath+"/records/",
        element: <RecordsView />,
        _saveScroll: true,
    },
    {
        path: appPath+"/records/input",
        element: <RecordsInputView />,
        _saveScroll: true,
    },
    {
        path: appPath+"/records/input/:sessionRef",
        element: <RecordsInputView />,
        _saveScroll: true,
    },

    //
    // Chatbot view
    //
    {
        path: appPath+"/chatbot/",
        element: <ChatbotView />,
        _saveScroll: true,
    },

    //
    // Special features
    //
    {
        path: appPath+"/age/calculator/",
        element: <AgeCalculatorView />,
    },

    {
        path: appPath+"/profile/",
        element: <ProfileView />,
    },

    //
    // Account-related user flow: registration, legal, etc.
    //
    {
        path: appPath+"/accept-terms/",
        element: <AcceptTermsView />,
        _hideDrawer: true,
    },
    {
        path: appPath+"/finish-registration/",
        element: <FinishRegistrationView />,
        _hideDrawer: true,
    },
    {
        path: appPath+"/subscriptions/",
        element: <SubscriptionsView />,
        _hideDrawer: true,
    },
    {
        path: appPath+"/subscriptions/history/",
        element: <SubscriptionsHistoryView />,
    },
    {path: appPath+"/subscriptions/payment/success/", element: <PaymentSuccessView />, _hideDrawer: true},
    {path: appPath+"/subscriptions/payment/inprogress/", element: <PaymentInprogressView />, _hideDrawer: true},
    {path: appPath+"/subscriptions/payment/failed/", element: <PaymentFailureView />, _hideDrawer: true},
    {path: appPath+"/subscriptions/payment/cancelled/", element: <PaymentCancelledView />, _hideDrawer: true},

    //
    // Other user flow
    //
    {
        path: appPath+"/intro/",
        element: <IntroView />,
        _hideDrawer: true,
    },

    //
    // Static pages
    //
    {
        path: appPath+"/company-details/",
        element: <CompanyDetailsView />,
    },


    //
    // Tester panel
    //
    {
        path: appPath+"/tester/",
        element: <TesterView />,
    },
    {
        path: appPath+"/tester/style/",
        element: <TestStyle />,
    },
    {
        path: appPath+"/tester/actions/",
        element: <TestActions />,
    },
    {
        path: appPath+"/tester/dynamics/cases/main/",
        element: <DynamicsCasesMain />,
    },
    {
        path: appPath+"/tester/dynamics/cases/typical/",
        element: <DynamicsCasesTypical />,
    },
    {
        path: appPath+"/tester/dynamics/cases/biohacker01/",
        element: <DynamicsCasesSelect dataId="biohacker01" />,
    },
    {
        path: appPath+"/tester/dynamics/cases/biohacker02/",
        element: <DynamicsCasesSelect dataId="biohacker02" />,
    },
    {
        path: appPath+"/tester/dynamics/cases/biohacker03/",
        element: <DynamicsCasesSelect dataId="biohacker03" />,
    },

    //
    // Default
    //
    {
        path: '*',
        element: <Navigate to={appPath+"/"} />,  // can't use route() here
    },
];
